import { decodeJwtPayload } from './jwt'

export type AdminSessionOwner = Readonly<{
  generation: number
  sessionId: string | null
  transition: string | null
}>
export type AdminSessionCredentials = {
  token: string
  sessionId: string
  expiresAt?: string
  expires_at?: string
}

let generation = 0
const listeners = new Set<() => void>()
const TRANSITION_KEY = 'admin_portal_auth_transition'
const loginOwners = new WeakMap<object, AdminSessionOwner>()

export class AdminSessionChangedError extends Error {
  constructor() { super('Admin authentication session changed') }
}

export class AdminAuthBusyError extends Error {
  constructor() { super('Previous authentication request is still finishing') }
}

export const readAdminToken = (): string | null => localStorage.getItem('admin_portal_token')
export const captureAdminSession = (): AdminSessionOwner => ({
  generation,
  sessionId: localStorage.getItem('admin_portal_session_id'),
  transition: localStorage.getItem(TRANSITION_KEY),
})
export const isCurrentAdminSession = (owner: AdminSessionOwner): boolean =>
  owner.generation === generation
  && owner.sessionId === localStorage.getItem('admin_portal_session_id')
  && owner.transition === localStorage.getItem(TRANSITION_KEY)

export function requireCurrentAdminSession(owner: AdminSessionOwner): void {
  if (!isCurrentAdminSession(owner)) throw new AdminSessionChangedError()
}

export function readAdminTokenExpiry(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = decodeJwtPayload<{ exp?: number; iat?: number; nbf?: number }>(token)
    if (!Number.isFinite(payload.exp) || !Number.isFinite(payload.iat)
      || (payload.nbf !== undefined && (!Number.isFinite(payload.nbf)
        || payload.nbf > Date.now() / 1000))) return null
    return payload.exp! * 1000
  } catch { return null }
}

export const isAdminTokenValid = (token: string | null): token is string => {
  const expiry = readAdminTokenExpiry(token)
  return expiry !== null && expiry > Date.now()
}

export function subscribeAdminSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function notify(): void { listeners.forEach(listener => listener()) }

export function clearAdminSession(owner = captureAdminSession()): boolean {
  if (!isCurrentAdminSession(owner)) return false
  generation += 1
  try {
    // Old clients have no transition key. Free credential storage before allocating it,
    // so a full localStorage cannot prevent clearing an expired legacy session.
    localStorage.removeItem('admin_portal_token')
    localStorage.removeItem('admin_portal_expires_at')
    localStorage.removeItem('admin_portal_refresh_token')
    localStorage.removeItem('admin_portal_session_id')
    localStorage.setItem(TRANSITION_KEY, crypto.randomUUID())
  } finally {
    notify()
  }
  return true
}

function storeSession(credentials: AdminSessionCredentials): void {
  if (!isAdminTokenValid(credentials.token) || typeof credentials.sessionId !== 'string'
    || !credentials.sessionId.trim()) throw new Error('Invalid admin session response')
  const expiresAt = new Date(readAdminTokenExpiry(credentials.token)!).toISOString()
  localStorage.setItem('admin_portal_token', credentials.token)
  localStorage.setItem('admin_portal_expires_at', expiresAt)
  localStorage.removeItem('admin_portal_refresh_token')
  // The session ID is the publication marker for other tabs observing storage events.
  localStorage.setItem('admin_portal_session_id', credentials.sessionId)
}

export function commitAdminRefresh(credentials: AdminSessionCredentials, owner: AdminSessionOwner): void {
  requireCurrentAdminSession(owner)
  if (!owner.sessionId || credentials.sessionId !== owner.sessionId) {
    throw new Error('Admin refresh returned a different session')
  }
  storeSession(credentials)
  notify()
}

export function commitAdminLogin(credentials: AdminSessionCredentials): void {
  storeSession(credentials)
  generation += 1
  localStorage.setItem(TRANSITION_KEY, crypto.randomUUID())
  notify()
}

export function registerAdminLoginResult<T extends AdminSessionCredentials>(
  credentials: T, owner: AdminSessionOwner,
): T {
  requireCurrentAdminSession(owner)
  loginOwners.set(credentials, owner)
  return credentials
}

export function commitAdminLoginResult(credentials: AdminSessionCredentials): void {
  const owner = loginOwners.get(credentials)
  if (!owner) throw new AdminSessionChangedError()
  requireCurrentAdminSession(owner)
  if (owner.sessionId !== credentials.sessionId || readAdminToken() !== credentials.token) {
    commitAdminLogin(credentials)
  }
  loginOwners.delete(credentials)
}

export function waitForAdminRefreshRetry(owner: AdminSessionOwner, delayMs: number): Promise<void> {
  requireCurrentAdminSession(owner)
  return new Promise(resolve => {
    const finish = () => { clearTimeout(timer); unsubscribe(); resolve() }
    const timer = setTimeout(finish, delayMs)
    const unsubscribe = subscribeAdminSession(() => {
      if (!isCurrentAdminSession(owner)) finish()
    })
  })
}
